import type { SimilarTest } from '@@/api/learnings/similar/similar-types';
import type { Experiment } from 'types';
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { debounce } from 'helpers/debounce';
import { useWebsites } from '@/state/useWebsites';
import { isPreStart } from 'helpers/experiment-status';

/** Long enough that typing a sentence is one request, short enough to answer before they look up. */
export const SIMILAR_DEBOUNCE_MS = 600;

type UseSimilarTestsParams = { experiment: Pick<Experiment, 'id' | 'websiteId' | 'status'> };

/**
 * Past and current tests on the website that a draft resembles, for the hypothesis card.
 *
 * Only for a Draft on the app's current website with learnings on: the matching is meant to stop
 * a website re-running its own losses before it launches, and it is that website's history. The
 * text the person is typing reaches the request through `setText`, which is called from the
 * field's change handler and debounced, so the request is keyed on the settled text and nothing
 * here needs an effect. The previous answer stays on screen while the next one loads.
 */
export const useSimilarTests = ({ experiment }: UseSimilarTestsParams) => {
  const client = useClient();
  const { website } = useWebsites();
  const [text, setSettledText] = useState('');
  // Created once: a debounced function made per render would never see its own previous call.
  const [settle] = useState(() => debounce((value: unknown) => setSettledText(String(value)), SIMILAR_DEBOUNCE_MS));

  const enabled = isPreStart(experiment.status) && !!website?.learningsEnabled && website.id === experiment.websiteId;

  const { data, isError } = useQuery<SimilarTest[]>({
    queryKey: ['LEARNINGS_SIMILAR', experiment.id, text],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: () =>
      client.get<SimilarTest[]>(
        `/learnings/${experiment.websiteId}/similar?experimentId=${experiment.id}&text=${encodeURIComponent(text)}`,
      ),
  });

  return {
    enabled,
    isError,
    matches: enabled ? (data ?? []) : [],
    /** The hypothesis as typed, saved or not. */
    setText: (value: string) => settle(value.trim()),
  };
};

export type SimilarTestsManager = ReturnType<typeof useSimilarTests>;
